import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "../api";
import { useAmazonContext } from "../../Contexts/AmazonContext";
import KnowledgeChallengeQuiz from "./KnowledgeChallengeQuiz";
import ChallengeCompleteCard from "./ChallengeCompleteCard";

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: "Quicksand", sans-serif;
  padding: 20px;
  background: ${(props) =>
    props.isDarkMode ? "var(--background-dark)" : "var(--background-light)"};
  transition: background 0.5s ease;
`;

const ContentWrapper = styled.div`
  max-width: 600px;
  width: 100%;
  margin-top: 20px;
`;

const BookCoversRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const BookCover = styled.img`
  width: 70px;
  height: auto;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  object-fit: cover;
`;

const ClickableBookCover = styled(motion.img)`
  width: 130px;
  height: auto;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  object-fit: cover;
  cursor: pointer;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  color: #8747d4;
  text-align: center;
  margin-bottom: 20px;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #555;
  text-align: center;
  line-height: 1.6;
`;

const RuleList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 20px 0;
`;

const RuleItem = styled.li`
  padding: 12px 16px;
  margin: 8px 0;
  background: #f3eefa;
  border-radius: 12px;
  font-size: 0.95rem;
  color: #333;
  display: flex;
  align-items: flex-start;
  gap: 10px;

  &::before {
    content: "${(props) => props.icon || "📌"}";
    flex-shrink: 0;
  }
`;

const ActionButton = styled(motion.button)`
  background: #8747d4;
  color: white;
  border: none;
  border-radius: 16px;
  padding: 16px 40px;
  font-size: 1.1rem;
  font-weight: 700;
  font-family: "Quicksand", sans-serif;
  cursor: pointer;
  box-shadow: 0 4px 0 #6535a3;
  position: relative;
  top: 0;
  display: block;
  margin: 20px auto 0;

  &:active {
    top: 4px;
    box-shadow: 0 0 0 #6535a3;
  }
`;


const CountdownNumber = styled(motion.div)`
  font-size: 8rem;
  font-weight: 800;
  color: #8747d4;
  text-align: center;
  position: absolute;
`;

const GoText = styled(motion.div)`
  font-size: 6rem;
  font-weight: 800;
  position: absolute;
  color: #4caf50;
  text-align: center;
`;



function convertToCloudFrontUrl(s3Url) {
  if (!s3Url) return s3Url;
  return s3Url.replace(
    "https://hhcourses-assets.s3.us-east-2.amazonaws.com",
    "https://d10jmd9bhq0s4v.cloudfront.net"
  );
}

function KnowledgeChallenge() {
  const history = useHistory();
  const { isDarkMode } = useAmazonContext();
  const [screen, setScreen] = useState("loading");
  const [challenge, setChallenge] = useState(null);
  const [courseImages, setCourseImages] = useState([]);
  const [courseCardTitles, setCourseCardTitles] = useState([]);
  const [allCoursesData, setAllCoursesData] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const scoreRef = useRef(0);
  const [timer, setTimer] = useState(0);
  const [countdownNum, setCountdownNum] = useState(3);
  const [missingCourses, setMissingCourses] = useState([]);
  const [existingAttempt, setExistingAttempt] = useState(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  const correctSound = useRef(
    new Audio("https://hhcourses-assets.s3.us-east-2.amazonaws.com/General/Audio/success11.mp3")
  );
  const incorrectSound = useRef(
    new Audio("https://hhcourses-assets.s3.us-east-2.amazonaws.com/General/Audio/wrongAnswer.mp3")
  );
  const tickingSound = useRef(
    new Audio("https://hhcourses-assets.s3.us-east-2.amazonaws.com/General/Audio/clockticking.mp3")
  );

  useEffect(() => {
    correctSound.current.load();
    incorrectSound.current.load();
    tickingSound.current.load();
    tickingSound.current.loop = true;

    return () => {
      tickingSound.current.pause();
      tickingSound.current.currentTime = 0;
    };
  }, []);

  useEffect(() => {
    loadChallenge();
  }, []);

  const loadChallenge = async () => {
    try {
      const res = await api.get("/knowledge-challenge");
      if (!res.data || !res.data.isActive) {
        setScreen("unavailable");
        return;
      }
      setChallenge(res.data);

      const userEmail = localStorage.getItem("userEmail");
      const attemptRes = await api.get("/knowledge-challenge/my-attempt", {
        params: { userEmail, challengeId: res.data._id },
      });
      if (attemptRes.data) {
        setExistingAttempt(attemptRes.data);
        setScreen("results");
        return;
      }

      let allCourses = [];
      let matched = [];
      try {
        const coursesRes = await api.get("/coursesData");
        allCourses = coursesRes.data;
        setAllCoursesData(allCourses);
        matched = (res.data.courses || []).map((title) =>
          allCourses.find(
            (c) => c.courseTitle.replace(/\s/g, "") === title.replace(/\s/g, "")
          )
        );
        setCourseImages(matched.map((m) => (m ? convertToCloudFrontUrl(m.courseImage) : null)).filter(Boolean));
        setCourseCardTitles(matched.map((m) => (m ? m.courseCardTitle || m.courseTitle : null)).filter(Boolean));
      } catch (e) {}

      const totalChaptersPerCourse = {};
      try {
        const sidebarPromises = matched.filter(Boolean).map(async (course) => {
          const courseKey = course.courseTitle.replace(/\s/g, "");
          const sidebarRes = await api.get(
            `/${courseKey}/sideBarData?collectionName=${course.courseTopicsCollectionName}&modelName=${course.courseTopicsModelName}`
          );
          const chapters = (sidebarRes.data || [])
            .map((header) => (header.entries || []).map((ch) => ch.topicNumber))
            .flat();
          totalChaptersPerCourse[courseKey] = chapters.length;
        });
        await Promise.all(sidebarPromises);
      } catch (e) {}

      let freshProgress = [];
      try {
        const progressRes = await api.get("/fetchChapterProgressRecord", {
          params: { userEmail },
        });
        freshProgress = progressRes.data || [];
      } catch (e) {
        freshProgress = [];
      }

      checkEligibility(res.data, freshProgress, totalChaptersPerCourse);
    } catch (error) {
      console.error("Error loading challenge:", error);
      setScreen("unavailable");
    }
  };

  const checkEligibility = (challengeData, progressData, totalChaptersPerCourse) => {
    if (!progressData || progressData.length === 0 || !challengeData.courses) {
      setMissingCourses(challengeData.courses || []);
      setScreen("not-eligible");
      return;
    }

    const missing = [];
    for (const courseTitle of challengeData.courses) {
      const courseKey = courseTitle.replace(/\s/g, "");
      const courseProgress = progressData.find(
        (cp) => cp.courseProgress && cp.courseProgress.replace(/\s/g, "") === courseKey
      );
      const completedChapters = courseProgress?.chapterProgress?.length || 0;
      const totalChapters = totalChaptersPerCourse[courseKey] || 0;

      if (totalChapters === 0 || completedChapters < totalChapters) {
        missing.push(courseTitle);
      }
    }

    if (missing.length > 0) {
      setMissingCourses(missing);
      setScreen("not-eligible");
    } else {
      setScreen("instructions");
    }
  };

  const startCountdown = () => {
    setScreen("countdown");
    setCountdownNum(3);
    let count = 3;
    const interval = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdownNum(count);
      } else if (count === 0) {
        setCountdownNum(0);
      } else {
        clearInterval(interval);
        startQuiz();
      }
    }, 1000);
  };

  const startQuiz = async () => {
    try {
      const userEmail = localStorage.getItem("userEmail");
      const res = await api.get("/knowledge-challenge/questions", {
        params: { userEmail, challengeId: challenge._id },
      });
      setQuestions(res.data);
      setCurrentIndex(0);
      setScore(0);
      scoreRef.current = 0;
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setTimer(Date.now() - startTimeRef.current);
      }, 100);
      tickingSound.current.currentTime = 0;
      tickingSound.current.play().catch(() => {});
      setScreen("quiz");
    } catch (error) {
      console.error("Error fetching questions:", error);
      setScreen("unavailable");
    }
  };

  const handleAnswerClick = (answerItem) => {
    if (answered) return;
    setSelectedAnswer(answerItem);
    setAnswered(true);
    if (answerItem.isCorrect) {
      scoreRef.current += 1;
      setScore(scoreRef.current);
      correctSound.current.play().catch(() => {});
    } else {
      incorrectSound.current.play().catch(() => {});
    }
  };

  const handleContinue = async () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setSelectedAnswer(null);
      setAnswered(false);
    } else {
      clearInterval(timerRef.current);
      tickingSound.current.pause();
      tickingSound.current.currentTime = 0;
      const finalTime = Date.now() - startTimeRef.current;
      setTimer(finalTime);
      await submitAttempt(finalTime);
      setScreen("results");
    }
  };

  const submitAttempt = async (finalTime) => {
    try {
      const userEmail = localStorage.getItem("userEmail");
      const givenName = localStorage.getItem("userGivenName") || "";
      const familyName = localStorage.getItem("userFamilyName") || "";
      await api.post("/knowledge-challenge/submit", {
        userEmail,
        challengeId: challenge._id,
        givenName,
        familyName,
        score: scoreRef.current,
        totalQuestions: questions.length,
        timeTaken: finalTime,
      });
    } catch (error) {
      console.error("Error submitting attempt:", error);
    }
  };

  const currentQuestion = questions[currentIndex];


  if (screen === "loading") {
    return (
      <PageContainer isDarkMode={isDarkMode}>
        <ContentWrapper>
          <Title>Loading...</Title>
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (screen === "unavailable") {
    return (
      <PageContainer isDarkMode={isDarkMode}>
        <ContentWrapper>
          <Title>No Active Challenge</Title>
          <Subtitle>There is no knowledge challenge available right now. Check back later!</Subtitle>
          <ActionButton
            whileTap={{ scale: 0.95 }}
            onClick={() => history.push("/home")}
          >
            Back to Home
          </ActionButton>
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (screen === "not-eligible") {
    const missingWithImages = missingCourses.map((title) => {
      const match = allCoursesData.find(
        (c) => c.courseTitle.replace(/\s/g, "") === title.replace(/\s/g, "")
      );
      return {
        title,
        image: match ? convertToCloudFrontUrl(match.courseImage) : null,
        route: match ? `/${match.courseTitle.replace(/\s/g, "")}` : null,
      };
    });

    return (
      <PageContainer isDarkMode={isDarkMode} style={{ justifyContent: "center" }}>
        <ContentWrapper>
          <Title>Complete These Courses First!</Title>
          <Subtitle>
            You need to complete the following before you can join the {challenge?.name || "Knowledge Challenge"}:
          </Subtitle>
          <BookCoversRow>
            {missingWithImages.map((course) =>
              course.image ? (
                <ClickableBookCover
                  key={course.title}
                  src={course.image}
                  alt={course.title}
                  loading="lazy"
                  onClick={() => course.route && history.push(course.route)}
                  animate={{ rotate: [0, -4, 4, -3, 3, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 1 }}
                  whileTap={{ scale: 0.9 }}
                />
              ) : null
            )}
          </BookCoversRow>
          {challenge?.endDate && (
            <Subtitle style={{ fontWeight: 700, marginTop: 10 }}>
              Hurry! The challenge ends on{" "}
              {new Date(challenge.endDate).toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </Subtitle>
          )}
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (screen === "instructions") {
    return (
      <PageContainer isDarkMode={isDarkMode}>
        <ContentWrapper>
          <Title>{challenge.name || "Knowledge Challenge"}</Title>
          {courseImages.length > 0 && (
            <BookCoversRow>
              {courseImages.map((img, idx) => (
                <BookCover
                  key={idx}
                  src={img}
                  alt="Course cover"
                  loading="lazy"
                  style={{ width: "110px", minWidth: "110px", maxWidth: "140px" }}
                />
              ))}
            </BookCoversRow>
          )}
          <Subtitle style={{ color: isDarkMode ? "#cfc8e8" : "#47414b" }}>
            Test your knowledge and compete for the top spot!
          </Subtitle>
          <RuleList>
            <RuleItem icon="🧠">
              Test your knowledge for the{" "}
              {courseCardTitles.length > 0
                ? courseCardTitles.join(", ")
                : challenge.courses?.join(", ") || ""}{" "}
              course!
            </RuleItem>
            <RuleItem icon="🏆">One winner takes it all!</RuleItem>
            <RuleItem icon="🥇">
              The winner gets a shiny medal reflected on the leaderboard.
            </RuleItem>
            <RuleItem icon="📊">Top scorer wins. If there's a tie, fastest time wins.</RuleItem>
            <RuleItem icon="⚠️">You can only take the challenge once!</RuleItem>
            <RuleItem icon="⏰">
              Challenge ends:{" "}
              {challenge.endDate
                ? new Date(challenge.endDate).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "TBD"}
            </RuleItem>
          </RuleList>
          <ActionButton whileTap={{ scale: 0.95 }} onClick={startCountdown}>
            Start Challenge
          </ActionButton>
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (screen === "countdown") {
    return (
      <PageContainer
        isDarkMode={isDarkMode}
        style={{
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <AnimatePresence mode="wait">
          {countdownNum > 0 ? (
            <CountdownNumber
              key={countdownNum}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1, transition: { duration: 0.3 } }}
              exit={{ scale: 0.5, opacity: 0, transition: { duration: 0.15 } }}
            >
              {countdownNum}
            </CountdownNumber>
          ) : (
            <GoText
              key="go"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1, transition: { duration: 0.3 } }}
              exit={{ scale: 2, opacity: 0, transition: { duration: 0.15 } }}
            >
              GO!
            </GoText>
          )}
        </AnimatePresence>
      </PageContainer>
    );
  }

  if (screen === "quiz" && currentQuestion) {
    return (
      <KnowledgeChallengeQuiz
        questions={questions}
        currentIndex={currentIndex}
        timer={timer}
        onAnswer={handleAnswerClick}
        onContinue={handleContinue}
        answered={answered}
        selectedAnswer={selectedAnswer}
        isDarkMode={isDarkMode}
      />
    );
  }

  if (screen === "results") {
    const displayScore = existingAttempt ? existingAttempt.score : score;
    const displayTotal = existingAttempt ? existingAttempt.totalQuestions : questions.length;
    const displayTime = existingAttempt ? existingAttempt.timeTaken : timer;

    return (
      <PageContainer isDarkMode={isDarkMode} style={{ justifyContent: "center" }}>
        <ChallengeCompleteCard
          score={displayScore}
          totalQuestions={displayTotal}
          timeTaken={displayTime}
          endDate={challenge?.endDate}
          onBackToHome={() => history.push("/home")}
        />
      </PageContainer>
    );
  }

  return null;
}

export default KnowledgeChallenge;
